import { FormErrorMessage, Text } from '@chakra-ui/core'
import { StyledFormControl, StyledFormLabel } from 'components/AdminConsole/commonUI/styles'
import CustomDatePicker from 'components/CommonComponents/CustomDatePicker'
import React, { useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import dayjs from 'dayjs'
import useLocaleText from 'components/useLocaleText'

interface CampaignsDatePickerProps {
  name: string
  label: string
  showTimeInput?: boolean
  after?: string
  before?: string
  label_after?: string
  label_before?: string
  defaultTime?: string
  rangeValidation?: {
    startField: string
    endField: string
    maxDays: number
  }
}

export const CampaignsDatePicker = ({
  name,
  label,
  after,
  before,
  showTimeInput = true,
  defaultTime,
  rangeValidation,
  label_after,
  label_before,
}: CampaignsDatePickerProps) => {
  const { control, errors, getValues, clearError } = useFormContext()
  const hasError = !!errors.products_cluster_data?.[name]
  const [isDateSet, setIsDateSet] = useState(false)
  const t_after_error = useLocaleText('t_after_error', true)
  const t_before_error = useLocaleText('t_before_error', true)
  const t_is_required = useLocaleText('is_required', true)
  const t_range_between = useLocaleText('range_between', true)
  const t_and = useLocaleText('and', true)
  const t_cannot_exceed = useLocaleText('cannot_exceed', true)
  const t_days = useLocaleText('days', true)

  const handleDateChange = (selectedDate: Date | null) => {
    if (!selectedDate) {
      setIsDateSet(false)

      return null
    }

    if (defaultTime && !isDateSet) {
      const [hours, minutes] = defaultTime.split(':').map(Number)
      setIsDateSet(true)
      return dayjs(selectedDate).hour(hours).minute(minutes).toDate()
    }

    if (!isDateSet) {
      setIsDateSet(true)
    }
    return selectedDate
  }

  return (
    <StyledFormControl isInvalid={hasError} w={{ base: '100%', md: '50%' }} h="115px" mb={0}>
      <StyledFormLabel required>
        <Text>{label}</Text>
      </StyledFormLabel>

      <Controller
        name={`products_cluster_data.${name}`}
        control={control}
        rules={{
          required: `${label} ${t_is_required}`,
          validate: (value) => {
            if (!value) return `${label} ${t_is_required}`

            const current = dayjs(value)

            if (after) {
              const afterVal = getValues(`products_cluster_data.${after}`)
              if (afterVal && current.isBefore(dayjs(afterVal))) {
                return `${label} ${t_after_error} ${label_after?.replace(/_/g, ' ')}`
              }
            }

            if (before) {
              const beforeVal = getValues(`products_cluster_data.${before}`)
              if (beforeVal && current.isAfter(dayjs(beforeVal))) {
                return `${label} ${t_before_error} ${label_before?.replace(/_/g, ' ')}`
              }
            }

            if (rangeValidation) {
              const { startField, endField, maxDays } = rangeValidation
              const startVal = getValues(`products_cluster_data.${startField}`)
              const endVal = getValues(`products_cluster_data.${endField}`)

              if (startVal && endVal) {
                const startDate = dayjs(startVal)
                const endDate = dayjs(endVal)
                const diffInDays = endDate.diff(startDate, 'day')

                if (diffInDays > maxDays) {
                  return `${t_range_between} ${startField.replace(/_/g, ' ')} ${t_and} ${endField.replace(
                    /_/g,
                    ' '
                  )} ${t_cannot_exceed} ${maxDays} ${t_days}`
                }
              }
            }
            clearError(`products_cluster_data.${name}`)
            return true
          },
        }}
        as={CustomDatePicker}
        onChange={([selectedDate]) => handleDateChange(selectedDate)}
        showTimeInput={showTimeInput}
        timeInputLabel="Time:"
        timeFormat="HH:mm"
        dateFormat="MM/dd/yyyy HH:mm"
        shouldCloseOnSelect={false}
      />

      <FormErrorMessage
        style={{ columnGap: '10px' }}
        alignItems="flex-start"
        color="red.500"
        fontSize="sm"
        mt={1}
        h="34px"
      >
        {errors.products_cluster_data?.[name]?.message}
      </FormErrorMessage>
    </StyledFormControl>
  )
}
